import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage, handleApiResponse, notify } from '@/functions';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';
import type { IDeleteTransactionParams } from '@/interfaces';

export const useRequestDeleteTransaction = () => {
	const queryClient = useQueryClient();
	const { isPending, error, mutateAsync } = useMutation({
		mutationKey: [routes.api.transactions, 'delete'],
		mutationFn: ({ _id, isOwner }: IDeleteTransactionParams) =>
			apiClient
				.delete<void>(`${routes.api.transactions}/${_id}${isOwner ? '/owner' : ''}`)
				.then(handleApiResponse),

		onMutate: async ({ _id }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: [routes.api.transactions] });

			// Snapshot previous value for potential rollback
			const previousData = queryClient.getQueriesData({ queryKey: [routes.api.transactions] });

			// Optimistically update cache - remove item immediately
			queryClient.setQueriesData({ queryKey: [routes.api.transactions] }, (oldData: any) => {
				if (!oldData?.data) return oldData;
				return {
					...oldData,
					data: oldData.data.filter((transaction: any) => transaction._id !== _id)
				};
			});

			return { previousData };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [routes.api.accountingSummary] });
			notify('La transaction a été supprimée avec succès');
		},
		onError: (requestError, _variables, context) => {
			// Rollback on error - restore previous data
			if (context?.previousData) {
				context.previousData.forEach(([queryKey, data]) => {
					queryClient.setQueryData(queryKey, data);
				});
			}
			notify(getApiErrorMessage(requestError, 'Erreur lors de la suppression de la transaction'), 'error');
		}
	});

	const trigger = (data: IDeleteTransactionParams) => mutateAsync(data);

	return {
		isLoading: isPending,
		trigger,
		error
	};
};
